// @ts-strict-ignore
import { useCallback } from 'react';
import type {
  ComponentPropsWithoutRef,
  ComponentType,
  CSSProperties,
  ReactNode,
} from 'react';

import { SvgArrowThinRight, SvgReload } from '@actual-app/components/icons/v1';
import { theme } from '@actual-app/components/theme';
import { View } from '@actual-app/components/view';
import { css } from '@emotion/css';

import { CellValue, CellValueText } from '#components/spreadsheet/CellValue';
import { useFeatureFlag } from '#hooks/useFeatureFlag';
import { useSheetValue } from '#hooks/useSheetValue';
import type { Binding } from '#spreadsheet';

import { GoalStatus } from './GoalStatus';
import { makeBalanceAmountStyle } from './util';

type BalanceIndicatorProps = {
  style?: CSSProperties;
};

function BalanceIndicatorWrapper({
  style,
  children,
}: BalanceIndicatorProps & { children: ReactNode }) {
  return (
    <View
      style={{
        marginLeft: 2,
        display: 'inline-flex',
        justifyContent: 'center',
        alignItems: 'center',
        ...style,
      }}
    >
      {children}
    </View>
  );
}

export function CarryoverIndicator({ style }: BalanceIndicatorProps) {
  return (
    <BalanceIndicatorWrapper style={style}>
      <SvgArrowThinRight
        width={style?.width || 7}
        height={style?.height || 7}
        style={{ color: style?.color }}
      />
    </BalanceIndicatorWrapper>
  );
}

export function TransferPositiveIndicator({ style }: BalanceIndicatorProps) {
  return (
    <BalanceIndicatorWrapper style={style}>
      <SvgReload
        width={style?.width || 8}
        height={style?.height || 8}
        style={{ color: style?.color, transform: 'scaleX(-1)' }}
      />
    </BalanceIndicatorWrapper>
  );
}

type CellValueChildren = ComponentPropsWithoutRef<typeof CellValue>['children'];

type ChildrenWithClassName = (
  props: Parameters<CellValueChildren>[0] & {
    className: string;
  },
) => ReturnType<CellValueChildren>;

type BalanceWithCarryoverProps = Omit<
  ComponentPropsWithoutRef<typeof CellValue>,
  'children' | 'binding'
> & {
  children?: ChildrenWithClassName;
  carryover: Binding<'envelope-budget' | 'tracking-budget', 'carryover'>;
  transferPositive: Binding<
    'envelope-budget' | 'tracking-budget',
    'transfer-positive'
  >;
  /**
   * Expense category balance binding is `leftover`,
   * while income category balance binding is `sum-amount`.
   */
  balance: Binding<
    'envelope-budget' | 'tracking-budget',
    'leftover' | 'sum-amount'
  >;
  goal: Binding<'envelope-budget' | 'tracking-budget', 'goal'>;
  budgeted: Binding<'envelope-budget' | 'tracking-budget', 'budget'>;
  longGoal: Binding<'envelope-budget' | 'tracking-budget', 'long-goal'>;
  isDisabled?: boolean;
  shouldInlineGoalStatus?: boolean;
  CarryoverIndicator?: ComponentType<BalanceIndicatorProps>;
  TransferPositiveIndicator?: ComponentType<BalanceIndicatorProps>;
};

export function BalanceWithCarryover({
  carryover,
  transferPositive,
  balance,
  goal,
  budgeted,
  longGoal,
  isDisabled,
  shouldInlineGoalStatus,
  CarryoverIndicator: CarryoverIndicatorComponent = CarryoverIndicator,
  TransferPositiveIndicator:
    TransferPositiveIndicatorComponent = TransferPositiveIndicator,
  children,
  ...props
}: BalanceWithCarryoverProps) {
  const carryoverValue = useSheetValue(carryover);
  const transferPositiveValue = useSheetValue(transferPositive);
  const goalValue = useSheetValue(goal);
  const budgetedValue = useSheetValue(budgeted);
  const longGoalValue = useSheetValue(longGoal);
  const isGoalTemplatesEnabled = useFeatureFlag('goalTemplatesEnabled');
  const getBalanceAmountStyle = useCallback(
    (balanceValue: number) =>
      makeBalanceAmountStyle(
        balanceValue,
        isGoalTemplatesEnabled ? goalValue : null,
        longGoalValue === 1 ? balanceValue : budgetedValue,
      ),
    [budgetedValue, goalValue, isGoalTemplatesEnabled, longGoalValue],
  );

  const getDefaultClassName = useCallback(
    (balanceValue: number) => {
      const indicatorCount =
        Number(Boolean(transferPositiveValue)) +
        Number(Boolean(carryoverValue));

      return css({
        ...getBalanceAmountStyle(balanceValue),
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        textAlign: 'right',
        ...(indicatorCount > 1 && {
          paddingRight: 8,
        }),
        ...(!isDisabled && {
          cursor: 'pointer',
          ':hover': { textDecoration: 'underline' },
        }),
      });
    },
    [carryoverValue, getBalanceAmountStyle, isDisabled, transferPositiveValue],
  );

  return (
    <CellValue binding={balance} type="financial" {...props}>
      {({ type, name, value: balanceValue }) => (
        <>
          <View style={{ minHeight: 'auto', flexShrink: 0, maxWidth: '100%' }}>
            {children ? (
              children({
                type,
                name,
                value: balanceValue,
                className: getDefaultClassName(balanceValue),
              })
            ) : (
              <CellValueText
                type={type}
                name={name}
                value={balanceValue}
                className={getDefaultClassName(balanceValue)}
              />
            )}
          </View>

          {(transferPositiveValue || carryoverValue) && (
            <View
              style={{
                position: 'absolute',
                right: '-4px',
                alignSelf: 'center',
                justifyContent: 'center',
                alignItems: 'center',
                flexDirection: 'row',
                top: 0,
                bottom: 0,
              }}
            >
              {transferPositiveValue && (
                <TransferPositiveIndicatorComponent
                  style={getBalanceAmountStyle(balanceValue)}
                />
              )}
              {carryoverValue && (
                <CarryoverIndicatorComponent
                  style={getBalanceAmountStyle(balanceValue)}
                />
              )}
            </View>
          )}
          {shouldInlineGoalStatus &&
            isGoalTemplatesEnabled &&
            goalValue != null && (
              <>
                <View
                  style={{
                    borderTop: '1px solid ' + theme.tableBorderSeparator,
                    width: '160px',
                    margin: '3px 0px',
                  }}
                />
                <GoalStatus
                  balance={balance}
                  goal={goal}
                  budgeted={budgeted}
                  longGoal={longGoal}
                />
              </>
            )}
        </>
      )}
    </CellValue>
  );
}
